
import { api } from './base';
import { getValidToken } from '@/features/auth';
import { config } from '@/shared/lib/config';
import type { FolderAccessType } from './form-schema';

export interface FolderNode {
  id: string;
  parentId: string | null;
  segment: string;
  path: string;
  depth: number;
  committeeId: string | null;
  committeeName: string | null;
  effectiveCommitteeId: string | null;
  effectiveCommitteeName: string | null;
  committeeInherited: boolean;
  visible: boolean;
  effectiveVisible: boolean;
  archivedAt: string | null;
  grantCount: number;
}

export interface FolderPick {
  id: string;
  parentId: string | null;
  segment: string;
  path: string;
  depth: number;
  committeeId: string | null;
  committeeName: string | null;
}

export interface FolderMatch {
  path: string;
  committeeId: string | null;
  committeeName: string | null;
  matchedPath: string | null;
  known: boolean;
  alreadyGranted: boolean;
}

export interface FolderGrant {
  id: string;
  subjectUserId: string | null;
  subjectName: string | null;
  subjectEmail: string | null;
  subjectExternal?: boolean;
  folderNodeId: string | null;
  path: string;
  accessType: FolderAccessType;
  ticketId: string | null;
  grantedBy: string;
  grantedByName: string | null;
  grantedAt: string;
  validUntil: string | null;
  revokedAt: string | null;
  expired: boolean;
}

export interface FolderImportReport {
  created: string[];
  existing: string[];
  implied: string[];
  errors: string[];
  applied: boolean;
}

export interface FolderGrantFilters {
  subjectUserId?: string;
  path?: string;
  ticketId?: string;
  state?: 'active' | 'expired' | 'revoked' | 'all';
  limit?: number;
  offset?: number;
}

export const foldersApi = {
  tree: () => api.get<FolderPick[]>('/api/v1/folders/tree'),

  resolve: (paths: string[]) => {
    const query = paths.map((path) => `path=${encodeURIComponent(path)}`).join('&');
    return api.get<FolderMatch[]>(`/api/v1/folders/resolve?${query}`);
  },

  myGrants: () => api.get<FolderGrant[]>('/api/v1/folders/my-grants'),

  grantsOfUser: (userId: string) =>
    api.get<FolderGrant[]>(`/api/v1/users/${userId}/folder-grants`),
};

export interface RequestedAccess {
  path: string;
  accessType: FolderAccessType | null;
  subject: { id?: string; label: string } | null;
}

export interface GrantInput {
  subjectUserId?: string | null;
  subjectLabel?: string | null;
  path: string;
  accessType: FolderAccessType;
  validUntil?: string | null;
}

export const ticketFoldersApi = {
  ofTicket: (ticketId: string) =>
    api.get<{ grants: FolderGrant[]; requested: RequestedAccess[]; paths: string[] }>(
      `/api/v1/tickets/${ticketId}/folder-grants`,
    ),

  grant: (ticketId: string, input: GrantInput) =>
    api.post<FolderGrant>(`/api/v1/tickets/${ticketId}/folder-grants`, input),

  grantMany: (ticketId: string, items: GrantInput[]) =>
    api.post<FolderGrant[]>(`/api/v1/tickets/${ticketId}/folder-grants`, { items }),
};

export const adminFoldersApi = {
  list: (includeArchived = false) =>
    api.get<FolderNode[]>(
      `/api/v1/admin/folders${includeArchived ? '?includeArchived=true' : ''}`,
    ),

  create: (input: {
    parentId?: string | null;
    segment: string;
    committeeId?: string | null;
    visible?: boolean;
  }) => api.post<FolderNode>('/api/v1/admin/folders', input),

  update: (
    id: string,
    patch: {
      segment?: string;
      committeeId?: string | null;
      visible?: boolean;
      archived?: boolean;
    },
  ) => api.patch<FolderNode>(`/api/v1/admin/folders/${id}`, patch),

  remove: (id: string, recursive = false) =>
    api.delete<void>(`/api/v1/admin/folders/${id}${recursive ? '?recursive=true' : ''}`),

  importPaths: (input: {
    text: string;
    committeeId?: string | null;
    visible?: boolean;
    preview?: boolean;
  }) => api.post<FolderImportReport>('/api/v1/admin/folders/import', input),

  downloadExport: async (): Promise<void> => {
    const token = await getValidToken();
    const res = await fetch(`${config.apiUrl}/api/v1/admin/folders/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) throw new Error('Не удалось выгрузить пути');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'folders.txt';
    link.click();
    URL.revokeObjectURL(url);
  },

  grants: (filters: FolderGrantFilters = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') query.set(key, String(value));
    }
    const suffix = query.toString() ? `?${query}` : '';
    return api.get<{ items: FolderGrant[]; total: number }>(
      `/api/v1/admin/folders/grants${suffix}`,
    );
  },

  createGrant: (input: {
    subjectUserId: string;
    path: string;
    accessType: FolderAccessType;
    ticketId?: string | null;
    validUntil?: string | null;
  }) => api.post<FolderGrant>('/api/v1/admin/folders/grants', input),
};
